import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";

// Custom hook for animated rating
export const useAnimatedRating = (targetValue, isVisible) => {
  const $ = _c(11);
  const [displayValue, setDisplayValue] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  let t0;
  if ($[0] !== animatedProgress || $[1] !== displayValue || $[2] !== isVisible || $[3] !== targetValue) {
    t0 = () => {
      if (!isVisible) {
        return;
      }
      if (displayValue === targetValue && animatedProgress === targetValue) {
        return;
      }
      const startValue = displayValue;
      const difference = targetValue - startValue;
      const startTime = Date.now();
      let animationFrame;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / 1200, 1);
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + difference * easeOutCubic;
        const roundedValue = Math.round(currentValue);
        setDisplayValue(roundedValue);
        setAnimatedProgress(currentValue);
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setDisplayValue(targetValue);
          setAnimatedProgress(targetValue);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    };
    $[0] = animatedProgress;
    $[1] = displayValue;
    $[2] = isVisible;
    $[3] = targetValue;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isVisible || $[6] !== targetValue) {
    t1 = [targetValue, isVisible];
    $[5] = isVisible;
    $[6] = targetValue;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
  let t2;
  if ($[8] !== animatedProgress || $[9] !== displayValue) {
    t2 = {
      displayValue,
      animatedProgress
    };
    $[8] = animatedProgress;
    $[9] = displayValue;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};

// Custom hook for intersection observer
export const useIntersectionObserver = (ref, isExpanded, hasBeenVisible, setIsVisible, setHasBeenVisible) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== hasBeenVisible || $[1] !== isExpanded || $[2] !== ref || $[3] !== setHasBeenVisible || $[4] !== setIsVisible) {
    t0 = () => {
      if (!ref.current || !isExpanded) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && isExpanded && !hasBeenVisible) {
            setIsVisible(true);
            setHasBeenVisible(true);
          }
        });
      }, {
        threshold: 0.2,
        rootMargin: "0px"
      });
      observer.observe(ref.current);
      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    };
    $[0] = hasBeenVisible;
    $[1] = isExpanded;
    $[2] = ref;
    $[3] = setHasBeenVisible;
    $[4] = setIsVisible;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== hasBeenVisible || $[7] !== isExpanded) {
    t1 = [isExpanded, hasBeenVisible];
    $[6] = hasBeenVisible;
    $[7] = isExpanded;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
};